/**
 * Strict HTML whitelist sanitizer for user-edited/pasted content.
 * Only allows: <strong>, <b>, <em>, <i>, <a>, <br>, <span>.
 * Strips dangerous URL schemes and enforces rel="noopener noreferrer" on _blank links.
 */

const ALLOWED_TAGS = new Set(['strong', 'b', 'em', 'i', 'a', 'br', 'span']);

const ALLOWED_ATTRS = {
  a: new Set(['href', 'target', 'rel', 'data-pb-client-text']),
  span: new Set(['class', 'data-pb-client'])
};

const SAFE_URL_SCHEMES = new Set(['http:', 'https:', 'mailto:', 'tel:', '']);
const DATA_ATTR_PREFIX = 'data-';
const BUILDER_DATA_PREFIX = 'data-pb-';

export function sanitize(html) {
  if (!html || typeof html !== 'string') return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  walkAndStrip(doc.body);
  return doc.body.innerHTML;
}

function walkAndStrip(node) {
  const children = Array.from(node.childNodes);
  for (const child of children) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        unwrapElement(child);
      } else {
        stripAttributes(child, tag);
        walkAndStrip(child);
      }
    }
  }

  validateLinks(node);
}

function unwrapElement(el) {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) {
    parent.insertBefore(el.firstChild, el);
  }
  parent.removeChild(el);
  for (const child of Array.from(parent.childNodes)) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        unwrapElement(child);
      } else {
        walkAndStrip(child);
      }
    }
  }
}

function stripAttributes(el, tag) {
  const allowed = ALLOWED_ATTRS[tag];
  const attrs = Array.from(el.attributes);
  for (const attr of attrs) {
    const name = String(attr.name || '').toLowerCase();
    const isSafeTemplateDataAttr =
      name.startsWith(DATA_ATTR_PREFIX) && !name.startsWith(BUILDER_DATA_PREFIX);
    if (!isSafeTemplateDataAttr && (!allowed || !allowed.has(name))) {
      el.removeAttribute(attr.name);
    }
  }
}

function validateLinks(container) {
  const links = container.querySelectorAll('a[href]');
  for (const a of links) {
    try {
      const url = new URL(a.getAttribute('href'), 'https://placeholder.invalid');
      if (!SAFE_URL_SCHEMES.has(url.protocol)) {
        a.removeAttribute('href');
      }
    } catch {
      a.removeAttribute('href');
    }
    if (a.getAttribute('target') === '_blank') {
      a.setAttribute('rel', 'noopener noreferrer');
    }
  }
}

const HREF_ALLOWED_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * Validate a single href value (attribute-level, not full HTML).
 * Accepts site-relative paths (/about), fragments (#section), query-only (?x=1),
 * full URLs with allowed schemes, mailto:, and tel:. Rejects javascript:, data:,
 * protocol-relative (//evil.com), and parse errors.
 * Returns the cleaned (trimmed) href, or '' for rejected input.
 */
export function sanitizeHref(raw) {
  const value = String(raw == null ? '' : raw).trim();
  if (!value) return '';
  if (value.startsWith('//')) return '';
  if (value.startsWith('/') || value.startsWith('#') || value.startsWith('?')) {
    return value;
  }
  try {
    const parsed = new URL(value, 'https://placeholder.invalid');
    if (parsed.protocol === 'javascript:') return '';
    if (parsed.protocol === 'data:') return '';
    if (!HREF_ALLOWED_PROTOCOLS.has(parsed.protocol)) return '';
    return value;
  } catch {
    return '';
  }
}

/**
 * Validate an <iframe> embed src (Tier 4 Phase 5). STRICTER than sanitizeHref:
 * embeds execute third-party content, so only absolute https URLs pass — no
 * tel:/mailto:, no fragments, no site-relative paths, no protocol-relative,
 * no javascript:/data:. Returns the trimmed URL, or '' for rejected input.
 */
export function sanitizeEmbedSrc(raw) {
  const value = String(raw == null ? '' : raw).trim();
  if (!value) return '';
  if (value.startsWith('//')) return '';
  try {
    const parsed = new URL(value); // absolute-only: no base URL
    return parsed.protocol === 'https:' ? value : '';
  } catch {
    return '';
  }
}

/**
 * Validate a media src/poster for <video> (Tier 4 Phase 5). Absolute https
 * URLs or site-relative paths (self-hosted media in the exported site).
 * Rejects protocol-relative, javascript:, data:, and every non-https scheme.
 */
export function sanitizeMediaSrc(raw) {
  const value = String(raw == null ? '' : raw).trim();
  if (!value) return '';
  if (value.startsWith('//')) return '';
  if (value.startsWith('/')) return value;
  try {
    const parsed = new URL(value); // absolute-only: no base URL
    return parsed.protocol === 'https:' ? value : '';
  } catch {
    return '';
  }
}

/**
 * HTML-escape a single attribute value for safe interpolation into an HTML
 * attribute. Use when building markup by string concatenation; prefer
 * `element.setAttribute(name, value)` (no escaping needed) where possible.
 */
export function escapeAttr(value) {
  return String(value == null ? '' : value)
    .replaceAll('&', '&amp;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;');
}
